import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { useCart } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState({
    status: "checking",
    payment_status: null,
    amount_total: null,
  });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error" });
      return;
    }

    let cancelled = false;
    let timer = null;

    const poll = async (n) => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = r.data;
        if (data.payment_status === "paid") {
          setState({ ...data, status: "paid" });
          clearCart();
          return;
        }
        if (data.status === "expired") {
          setState({ ...data, status: "expired" });
          return;
        }
        if (n >= 8) {
          setState({ ...data, status: "timeout" });
          return;
        }
        setAttempts(n + 1);
        timer = setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setState({ status: "error" });
      }
    };

    poll(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      data-testid="success-page"
      className="bg-[#050505] text-white pt-32 min-h-screen flex items-center"
    >
      <div className="px-6 md:px-12 lg:px-24 w-full">
        <div className="max-w-2xl mx-auto text-center">
          {state.status === "checking" && (
            <div data-testid="success-checking">
              <Loader2 className="w-12 h-12 text-[#E8A851] mx-auto animate-spin mb-8" />
              <p className="eyebrow mb-4">Verifying payment</p>
              <h1 className="font-display text-3xl sm:text-4xl font-medium">
                Hang tight — confirming your order…
              </h1>
              <p className="text-sm text-zinc-500 mt-4">
                Attempt {attempts + 1} of 9
              </p>
            </div>
          )}

          {state.status === "paid" && (
            <div data-testid="success-paid">
              <CheckCircle2 className="w-14 h-14 text-[#E8A851] mx-auto mb-8" />
              <p className="eyebrow mb-4">Order Confirmed</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight">
                Thank you.
                <br />
                <span className="text-[#E8A851]">Aura is on the way.</span>
              </h1>
              <p className="mt-6 text-zinc-400 leading-relaxed">
                Your order has been received. We'll send a confirmation and
                tracking details to your email shortly.
              </p>
              <div className="mt-10 border border-white/10 p-6 bg-white/[0.02]">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
                  Amount Paid
                </p>
                <p className="font-display text-3xl font-semibold">
                  ${state.amount_total?.toFixed(2)}{" "}
                  <span className="text-sm text-zinc-500 uppercase">
                    {state.currency}
                  </span>
                </p>
              </div>
              <Link
                to="/"
                data-testid="success-home-link"
                className="btn-primary mt-10 inline-flex"
              >
                Back to home <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {(state.status === "expired" ||
            state.status === "error" ||
            state.status === "timeout") && (
            <div data-testid="success-failure">
              <XCircle className="w-14 h-14 text-red-400 mx-auto mb-8" />
              <p className="eyebrow mb-4">Payment not completed</p>
              <h1 className="font-display text-3xl sm:text-4xl font-medium">
                We couldn't confirm your payment.
              </h1>
              <p className="mt-6 text-zinc-400">
                {state.status === "expired"
                  ? "Your checkout session expired."
                  : "Please try again or contact support."}
              </p>
              <Link to="/cart" className="btn-primary mt-10 inline-flex">
                Return to cart <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
